import React, { useEffect, useState } from "react";
import {
  addDoc,
  collection,
  doc,
  updateDoc,
} from "firebase/firestore";
import {
  Backdrop,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import PowerSettingsNewIcon from "@mui/icons-material/PowerSettingsNew";
import Swal from "sweetalert2";
import { dbFirebase } from "../Conections/Firebase";
import { fetchCollection } from "../Utils/firestore";
import NavbarProject from "./NavbarProject";
import TableEdit from "./TableEdit";
import RowActionsMenu from "./RowActionsMenu";

const initialForm = { nombre: "" };

// Catálogo de brokers/inversiones (colección "lista_inversion"). El borrado es lógico:
// en vez de eliminar el documento (rompería el histórico de movimientos que lo referencian),
// se marca como inactivo y deja de aparecer como opción al registrar nuevos movimientos.
const Brokers = () => {
  const [brokers, setBrokers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [editando, setEditando] = useState(null);
  const [form, setForm] = useState(initialForm);

  const cargarBrokers = async () => {
    setLoading(true);
    try {
      const data = await fetchCollection("lista_inversion");
      data.sort((a, b) => (a.nombre || "").localeCompare(b.nombre || ""));
      setBrokers(data);
    } catch (error) {
      Swal.fire("Error", "No se pudo cargar el catálogo: " + error.message, "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    cargarBrokers();
  }, []);

  const abrirNuevo = () => {
    setEditando(null);
    setForm(initialForm);
    setOpen(true);
  };

  const abrirEditar = (broker) => {
    setEditando(broker);
    setForm({ nombre: broker.nombre || "" });
    setOpen(true);
  };

  const guardar = async () => {
    const nombre = (form.nombre || "").trim();

    if (!nombre) {
      Swal.fire("Falta el nombre", "Captura el nombre del broker o inversión.", "warning");
      return;
    }

    try {
      setLoading(true);
      if (editando) {
        await updateDoc(doc(dbFirebase, "lista_inversion", editando.id), { nombre });
      } else {
        await addDoc(collection(dbFirebase, "lista_inversion"), { nombre, activo: true });
      }

      setOpen(false);
      setForm(initialForm);
      setEditando(null);
      await cargarBrokers();

      Swal.fire({
        title: editando ? "Broker actualizado" : "Broker agregado",
        icon: "success",
        confirmButtonText: "Aceptar",
      });
    } catch (error) {
      Swal.fire("Error", "No se pudo guardar el broker: " + error.message, "error");
    } finally {
      setLoading(false);
    }
  };

  const alternarActivo = async (broker) => {
    const activar = broker.activo === false;

    const result = await Swal.fire({
      icon: "question",
      title: activar ? "Activar broker" : "Desactivar broker",
      text: activar
        ? "Volverá a aparecer como opción al registrar movimientos y rendimientos."
        : "Dejará de aparecer como opción al registrar movimientos y rendimientos. El histórico no se modifica.",
      showCancelButton: true,
      confirmButtonText: activar ? "Activar" : "Desactivar",
      cancelButtonText: "Cancelar",
    });

    if (!result.isConfirmed) {
      return;
    }

    try {
      setLoading(true);
      await updateDoc(doc(dbFirebase, "lista_inversion", broker.id), { activo: activar });
      await cargarBrokers();
    } catch (error) {
      Swal.fire("Error", "No se pudo actualizar el estado: " + error.message, "error");
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    { key: "nombre", header: "Nombre" },
    {
      key: "activo",
      header: "Estado",
      render: (value) =>
        value === false ? (
          <Chip label="Inactivo" size="small" />
        ) : (
          <Chip label="Activo" size="small" color="success" />
        ),
    },
    {
      key: "acciones",
      header: "Acciones",
      render: (_, row) => (
        <RowActionsMenu
          primary={{
            label: "Editar",
            variant: "outlined",
            onClick: () => abrirEditar(row),
          }}
          actions={[
            {
              label: row.activo === false ? "Activar" : "Desactivar",
              icon: <PowerSettingsNewIcon fontSize="small" />,
              color: row.activo === false ? "success" : "warning",
              onClick: () => alternarActivo(row),
            },
          ]}
        />
      ),
    },
  ];

  return (
    <>
      <NavbarProject />
      <Grid container spacing={2} sx={{ padding: 2 }}>
        <Grid item xs={12}>
          <Typography variant="h5">Brokers e inversiones</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Catálogo de inversiones/brokers usado en Movimientos y Rendimientos. Desactivar uno
            no borra su histórico, solo deja de ofrecerse como opción al registrar algo nuevo.
          </Typography>
        </Grid>

        <Grid item xs={12}>
          <Button variant="contained" startIcon={<AddIcon />} onClick={abrirNuevo} disabled={loading}>
            Agregar broker
          </Button>
        </Grid>

        <Grid item xs={12}>
          <TableEdit columns={columns} rows={brokers} />
        </Grid>
      </Grid>

      <Backdrop open={loading} sx={{ zIndex: (theme) => theme.zIndex.modal + 1 }}>
        <CircularProgress color="inherit" />
      </Backdrop>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{editando ? "Editar broker" : "Agregar broker"}</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            sx={{ mt: 2 }}
            label="Nombre"
            value={form.nombre}
            onChange={(e) => setForm({ nombre: e.target.value })}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} disabled={loading}>
            Cancelar
          </Button>
          <Button onClick={guardar} variant="contained" disabled={loading}>
            Guardar
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default Brokers;
